import { Input, Row, Col, Dropdown, Menu, Checkbox, Button, Select, Grid } from 'antd';
import React, { useRef, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { ButtonText, MenuItemText } from '../../../../shared/globalStyling/styledText';
import { DownOutlined } from '@ant-design/icons';
import DateFilterDropdown from './DateFilterDropdown';
import { METRICS_WINDOW_OPTIONS } from '../../utils/metricsWindow';
import {
  DATE_FILTER_PARAMS,
  readDateFilterParams,
} from '../../../../../core/components/advertiser/buildDateFilter';

const { useBreakpoint } = Grid;

const captionStyle = {
  fontSize: 10,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#8c8c8c',
  marginBottom: 4,
  whiteSpace: 'nowrap',
};

const groupStyleInline = {
  borderLeft: '1px solid #f0f0f0',
  paddingLeft: 16,
};

const groupStyleStacked = {
  borderTop: '1px solid #f0f0f0',
  paddingTop: 8,
};

const DATE_STORAGE_KEYS = {
  mode: 'advertiserMainDateMode',
  from: 'advertiserMainDateFrom',
  to: 'advertiserMainDateTo',
};

const readSavedDateFilter = () => {
  const mode = sessionStorage.getItem(DATE_STORAGE_KEYS.mode);
  const from = sessionStorage.getItem(DATE_STORAGE_KEYS.from);
  const to = sessionStorage.getItem(DATE_STORAGE_KEYS.to);
  return mode && from && to ? { mode, from, to } : null;
};

const saveDateFilter = (value) => {
  Object.keys(DATE_STORAGE_KEYS).forEach((key) =>
    sessionStorage.setItem(DATE_STORAGE_KEYS[key], value[key])
  );
};

const clearSavedDateFilter = () => {
  Object.values(DATE_STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
};

const setDateParams = (params, value) => {
  Object.keys(DATE_FILTER_PARAMS).forEach((key) => params.delete(DATE_FILTER_PARAMS[key]));
  if (value) {
    Object.keys(DATE_FILTER_PARAMS).forEach((key) =>
      params.set(DATE_FILTER_PARAMS[key], value[key])
    );
  }
};

const DropDownStatusToggle = withRouter(({ location, history, orgLevel }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs || screens.sm;

  let statuses = [
    { text: 'Live', key: 'LIVE' },
    { text: 'Finished', key: 'FINISHED' },
    { text: 'In Attribution', key: 'ATTRIBUTION' },
    { text: 'Paused', key: 'PAUSED' },
  ];

  if (orgLevel !== 'STANDARD') statuses.push({ text: 'Demo', key: 'DEMO' });
  const params = new URLSearchParams(location.search);
  const selectedStatus = params.get('status');

  return (
    <Dropdown
      trigger={['click']}
      overlay={
        <Menu selectedKeys={[selectedStatus]}>
          {statuses.map((status, i) => {
            const isSelected = selectedStatus === status.key;
            return (
              <Menu.Item
                key={`${status.text}-${i}`}
                onClick={() => {
                  if (selectedStatus) {
                    params.delete('status');
                    sessionStorage.removeItem('advertiserMainStatus');
                  }
                  if (!isSelected) {
                    params.append('status', status.key);
                    sessionStorage.setItem('advertiserMainStatus', status.key);
                  }
                  history.push(`/advertiser/main/1?${params.toString()}`);
                }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Checkbox
                  checked={isSelected}
                  style={{
                    visibility: isSelected ? 'visible' : 'hidden',
                    marginRight: '8px',
                  }}
                />
                <MenuItemText text={status.text} />
              </Menu.Item>
            );
          })}
        </Menu>
      }
    >
      <Button
        style={{
          fontSize: isMobile ? '12px' : '14px',
          width: '100%',
          height: isMobile ? '32px' : '36px',
          padding: isMobile ? '4px 8px' : undefined,
        }}
        shape="round"
        type={!!selectedStatus ? 'primary' : 'default'}
        ghost={!!selectedStatus ? true : false}
      >
        <ButtonText text={'Status'} />
        <DownOutlined color="black" />
      </Button>
    </Dropdown>
  );
});

const FilterBar = ({ location, history, orgLevel, metricsWindow, onMetricsWindowChange }) => {
  const screens = useBreakpoint();
  const isMobile = screens.xs || screens.sm;
  const searchRef = useRef(null);
  const params = new URLSearchParams(location.search);
  const [searchQuery, setSearchQuery] = useState(params.get('search') || '');
  const dateFilter = readDateFilterParams(params);

  useEffect(() => {
    if (!location.search) {
      const savedSearch = sessionStorage.getItem('advertiserMainSearch');
      const savedStatus = sessionStorage.getItem('advertiserMainStatus');
      const savedDateFilter = readSavedDateFilter();

      let hasChanges = false;
      const newParams = new URLSearchParams();

      if (savedStatus && !params.get('status')) {
        newParams.append('status', savedStatus);
        hasChanges = true;
      }

      if (savedSearch && !params.get('search')) {
        newParams.append('search', savedSearch);
        setSearchQuery(savedSearch);
        hasChanges = true;
      }

      if (savedDateFilter && !dateFilter) {
        setDateParams(newParams, savedDateFilter);
        hasChanges = true;
      }

      if (hasChanges) {
        history.replace(`/advertiser/main/1?${newParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateApply = (value) => {
    setDateParams(params, value);
    saveDateFilter(value);
    history.push(`/advertiser/main/1?${params.toString()}`);
  };

  const handleDateClear = () => {
    setDateParams(params, null);
    clearSavedDateFilter();
    history.push(`/advertiser/main/1?${params.toString()}`);
  };

  const controlHeight = isMobile ? '32px' : '36px';

  return (
    <Row gutter={isMobile ? [8, 8] : [16, 8]} align="bottom">
      <Col xs={24} sm={24} md={24} lg={17}>
        <div style={captionStyle}>Filters</div>
        <Row gutter={isMobile ? [8, 8] : [12, 8]}>
          <Col xs={12} sm={12} md={6} lg={6}>
            <DropDownStatusToggle orgLevel={orgLevel} />
          </Col>
          <Col xs={12} sm={12} md={7} lg={7}>
            <DateFilterDropdown
              value={dateFilter}
              onApply={handleDateApply}
              onClear={handleDateClear}
            />
          </Col>
          <Col xs={24} sm={24} md={11} lg={11}>
            <Input.Search
              placeholder={isMobile ? 'Search...' : 'Search Advertiser Funnels'}
              ref={searchRef}
              style={{
                width: '100%',
                height: controlHeight,
                fontSize: isMobile ? '12px' : '14px',
              }}
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={(search) => {
                if (params.get('search')) {
                  params.delete('search');
                  sessionStorage.removeItem('advertiserMainSearch');
                }
                if (search) {
                  params.append('search', search);
                  sessionStorage.setItem('advertiserMainSearch', search);
                  history.push(`/advertiser/main/1?${params.toString()}`);
                } else {
                  setSearchQuery('');
                  history.push(`/advertiser/main/1`);
                }
              }}
            />
          </Col>
        </Row>
      </Col>
      <Col xs={24} sm={24} md={24} lg={7}>
        <div style={isMobile ? groupStyleStacked : groupStyleInline}>
          <div style={captionStyle}>Metrics Quickview</div>
          <Select
            value={metricsWindow}
            options={METRICS_WINDOW_OPTIONS}
            dropdownMatchSelectWidth={false}
            style={{ width: '100%', height: controlHeight }}
            onChange={onMetricsWindowChange}
          />
        </div>
      </Col>
    </Row>
  );
};

export default withRouter(FilterBar);
